import React, { KeyboardEvent, useCallback, useContext, useRef, useLayoutEffect } from 'react'
import styled from 'styled-components'
import noop from 'lodash/noop'
import { Box } from '../../new/core-components/box'
import { SystemProps } from '../../new/theme'
import { OutsideClickHandler } from '../helpers/vite-imports-fix/OutsideClickHandler'
import { RootPortal } from './RootPortal'
import { popperContext, PopperStyles } from './Popover'

type ChildrenProps = {
  setPopperElement: React.Dispatch<HTMLElement | null>
} & PopperStyles

type ChildrenRenderer = (props: ChildrenProps) => React.ReactNode

interface Props extends Omit<SystemProps, 'children'> {
  autoFocus?: ConstrainBoolean
  children: React.ReactNode | ChildrenRenderer
  className?: string
  isPortal?: boolean
  nestedPortalSelectors?: string[]
  onOutsideClick?: (target: EventTarget | null) => void
}

const isChildrenRenderer = (children: unknown): children is ChildrenRenderer =>
  typeof children === 'function'

/** @deprecated Use children prop on Popover from @chilipiper/design-system/src/new instead */
export const PopoverTarget = ({
  autoFocus,
  children,
  onOutsideClick = noop,
  isPortal,
  nestedPortalSelectors = [],
  className,
  ...others
}: Props) => {
  const {
    triggerElem,
    popperStyles: { styles, attributes },
    setPopperElement,
  } = useContext(popperContext)
  const handleOutsideClick = useCallback(
    ({ target }: Event | KeyboardEvent<HTMLDivElement>) => {
      const hasNestedPortal = nestedPortalSelectors.some(s => document.activeElement?.closest(s))
      if (!(triggerElem?.contains(target as Node) || hasNestedPortal)) {
        onOutsideClick(target)
      }
    },
    [triggerElem, onOutsideClick, nestedPortalSelectors]
  )

  const contentRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    setPopperElement(contentRef?.current)
    if (autoFocus) {
      contentRef?.current?.focus()
    }
  }, [setPopperElement, autoFocus])

  const content = (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <BoxWithoutEvents
        tabIndex={-1}
        className={className}
        {...others}
        onKeyUp={(e: KeyboardEvent<HTMLDivElement>) => {
          e.key === 'Escape' && handleOutsideClick(e)
        }}
        ref={contentRef}
        style={styles.popper}
        {...attributes.popper}
      >
        <Box mb={3}>
          <BoxWithEvents>
            {isChildrenRenderer(children)
              ? children({ setPopperElement, styles, attributes })
              : children}
          </BoxWithEvents>
        </Box>
      </BoxWithoutEvents>
    </OutsideClickHandler>
  )

  if (isPortal) {
    return <RootPortal>{content}</RootPortal>
  }

  return content
}

const BoxWithoutEvents = styled(Box)`
  pointer-events: none;
`
const BoxWithEvents = styled(Box)`
  pointer-events: auto;
`
